import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { Radius, Spacing, Typography } from 'chatie-design-system';
import { Textarea } from 'chatie-design-system/form';
import axios from 'axios';

import SwipeableCharacterList from '@components/characters/SwipeableCharacterList';
import Modal, { ModalProps } from '@components/common/Modal';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

import { fetchDisplayPresets } from '@api/chatbot';
import { fetchEpisode, fetchEpisodes } from '@api/episode';
import {
  StoryChatRoomApiPayload,
  createAvailableCharacter,
  postStoryChatRoom,
} from '@api/story';
import { QUERY_KEYS } from '@constants';
import {
  ChatieData,
  IDisplayPreset,
  IEpisode,
  IStoryChatRoomCharacter,
  IStoryDetail,
} from '@model';
import { useMe } from '@utils/hook';
import { useWebView } from '@utils/webview';

type Step = 'episode-select' | 'chat-input';

const containerCSS = (theme: Theme) => css`
  max-height: 70vh;
  overflow: auto;

  .modal-title {
    text-align: center;
    margin-bottom: 4px;
    font-weight: 700;
    ${Typography.h4_sentence};
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .modal-subtitle {
    text-align: center;
    color: ${theme.var.gray500};
    margin-bottom: 16px;
    ${Typography.body12_sentence};
  }

  .episode-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    max-height: 40vh;
    overflow-y: auto;
  }

  .episode-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border-radius: ${Radius.medium}px;
    border: 1.5px solid ${theme.border.light};
    cursor: pointer;
    transition: all 0.15s;

    &.selected {
      border-color: ${theme.colors.main500};
      background: rgba(4, 200, 208, 0.08);
    }

    .episode-title {
      ${Typography.body14};
      font-weight: 500;
      color: ${theme.var.gray800};
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .episode-order {
      ${Typography.body12};
      font-weight: 600;
      color: ${theme.var.gray400};
      margin-right: 8px;
      flex-shrink: 0;
    }

    &.selected .episode-title,
    &.selected .episode-order {
      color: ${theme.colors.main500};
    }
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
    gap: 12px;

    .loading-text {
      ${Typography.body12};
      color: ${theme.var.gray500};
    }
  }

  .empty-message {
    text-align: center;
    color: ${theme.var.gray400};
    padding: 24px 0;
    ${Typography.body14};
  }

  .character-container {
    height: 70px;
    padding: 0;
  }

  .input-container {
    padding: ${Spacing.small}px 0 1px 0;
    .text-form {
      padding: 10px 12px;
      min-height: 39px;
      background: ${theme.var.gray100};
    }
    .text-form textarea::placeholder {
      color: ${theme.var.gray400};
    }
  }

  .preset-selector {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }

  .desc {
    text-align: center;
    color: ${theme.var.gray500};
    margin-bottom: ${Spacing.xsmall}px;
    font-weight: 400;
    ${Typography.body10};

    .price-highlight {
      font-size: 13px;
      font-weight: 700;
      background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .free-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    margin: 0 auto 8px;
    padding: 8px 16px;
    border-radius: 20px;
    background: linear-gradient(
      56.96deg,
      rgba(4, 200, 208, 0.12) 19.7%,
      rgba(21, 144, 212, 0.12) 80.3%
    );
    width: fit-content;

    span {
      ${Typography.body12};
      font-weight: 600;
      background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .free-badge-sub {
      ${Typography.body10};
      font-weight: 400;
      color: ${theme.var.gray500};
      -webkit-text-fill-color: ${theme.var.gray500};
      background: none;
    }
  }

  .btn-row {
    display: flex;
    flex-direction: column;
    margin-top: 12px;

    button {
      box-shadow: none;
    }

    .send-btn {
      margin-bottom: ${Spacing.small}px;
    }
  }
`;

const presetBtnCSS = (isSelected: boolean) => (theme: Theme) =>
  css`
    flex: 1;
    padding: 10px 12px;
    border-radius: ${Radius.medium}px;
    border: 1.5px solid
      ${isSelected ? theme.colors.main500 : theme.border.light};
    background: ${isSelected ? 'rgba(4, 200, 208, 0.08)' : theme.var.white};
    text-align: center;
    cursor: pointer;
    transition: all 0.15s;

    .preset-name {
      ${Typography.body14};
      font-weight: 600;
      color: ${isSelected ? theme.colors.main500 : theme.var.gray600};
    }
    .preset-price {
      ${Typography.body10};
      font-weight: 600;
      margin-top: 2px;
      color: ${isSelected ? theme.colors.main500 : theme.var.gray400};
    }
  `;

type Props = {
  story: IStoryDetail | null;
};

const FreeChatEpisodeSelectModal = ({
  story,
  ...props
}: ModalProps & Props) => {
  const { t } = useTranslation();
  const { openPageOrMove, web } = useWebView();
  const { data: me } = useMe();

  const scrollYRef = useRef(0);

  useEffect(() => {
    if (props.open) {
      scrollYRef.current = window.scrollY;
      web.setIsBottomInputPage(true);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = '100%';
    } else if (document.body.style.position === 'fixed') {
      web.setIsBottomInputPage(false);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollYRef.current);
    }
    return () => {
      if (document.body.style.position === 'fixed') {
        web.setIsBottomInputPage(false);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollYRef.current);
      }
    };
  }, [props.open, web]);

  const { open } = props;

  const [step, setStep] = useState<Step>('episode-select');
  const [selectedEpisode, setSelectedEpisode] = useState<IEpisode | null>(null);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(false);

  // Chat input state
  const [characters, setCharacters] = useState<IStoryChatRoomCharacter[]>([]);
  const [selectedCharacter, setSelectedCharacter] =
    useState<IStoryChatRoomCharacter | null>(null);
  const [chatieData, setChatieData] = useState<ChatieData | null>(null);
  const [value, setValue] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<IDisplayPreset | null>(
    null
  );

  // Queries
  const { data: episodesData, isLoading: isLoadingEpisodes } = useQuery(
    QUERY_KEYS.STORY.EPISODE_LIST(story?.id ?? 0, 'oldest'),
    () => fetchEpisodes(story!.id, 'oldest', 1, 100),
    { enabled: open && !!story?.id, suspense: false }
  );

  const { data: presets } = useQuery(
    QUERY_KEYS.CHAT_ROOM.PRESETS('episode-chat'),
    () => fetchDisplayPresets('episode-chat'),
    { suspense: false, enabled: !!me }
  );

  const { mutateAsync: createChatRoom } = useMutation(postStoryChatRoom);

  const episodes: IEpisode[] =
    episodesData?.items?.filter((ep) => ep.isFree) ?? [];

  const hasMultiplePresets = (presets?.length ?? 0) > 1;
  const hasFreePreset = presets?.some((p) => {
    const price = p.discount_message_price ?? p.message_price;
    return price === 0;
  });
  const selectedPrice = selectedPreset
    ? (selectedPreset.discount_message_price ?? selectedPreset.message_price)
    : 79;

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep('episode-select');
      setSelectedEpisode(null);
      setCharacters([]);
      setSelectedCharacter(null);
      setChatieData(null);
      setValue('');
      setSelectedPreset(null);
    }
  }, [open]);

  // Auto-select first episode
  useEffect(() => {
    if (episodes.length && !selectedEpisode && step === 'episode-select') {
      setSelectedEpisode(episodes[0]);
    }
  }, [episodes]);

  // Auto-select default preset
  useEffect(() => {
    if (presets?.length && !selectedPreset) {
      const defaultIdx = presets.findIndex((p) => p.is_selected);
      setSelectedPreset(defaultIdx > -1 ? presets[defaultIdx] : presets[0]);
    }
  }, [presets]);

  const handleSelectEpisode = async () => {
    if (!selectedEpisode || !story) return;

    setIsLoadingCharacters(true);
    setStep('chat-input');

    try {
      // 1. Fetch episode detail
      const episodeDetail = await fetchEpisode(story.id, selectedEpisode.id);
      const content = episodeDetail.content;
      if (!content?.attachment?.url) {
        setStep('episode-select');
        setIsLoadingCharacters(false);
        return;
      }

      // 2. Download content JSON
      const { data: draft } = await axios.get<ChatieData>(
        content.attachment.url
      );

      setChatieData(draft);

      // 3. Get available characters
      const result = await createAvailableCharacter(draft);
      if (result?.characters?.length) {
        setCharacters(result.characters);
        setSelectedCharacter(result.characters[0]);
      }
    } catch (err) {
      console.error('[FreeChatModal] Failed to load characters:', err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        alert(t('이 회차에는 캐릭터가 부족해요. 다른 회차를 선택해주세요.'));
      } else {
        alert(t('오류가 발생했어요. 잠시 후 다시 시도해주세요.'));
      }
      setStep('episode-select');
    } finally {
      setIsLoadingCharacters(false);
    }
  };

  const handleSend = () => {
    if (!selectedCharacter || !value || !story || !selectedEpisode || !chatieData)
      return;

    const payload: StoryChatRoomApiPayload = {
      story_id: story.id,
      episode_id: selectedEpisode.id,
      episode_json: chatieData,
      profile_uuid: selectedCharacter.id,
    };

    createChatRoom(payload, {
      onSuccess(data) {
        props.onClose();
        setTimeout(() => {
          openPageOrMove(
            `/stories/${story.id}/episodes/${selectedEpisode.id}/chat-rooms/${data?.result?.id}/?first_message=${encodeURIComponent(value)}&title=${encodeURIComponent(story.title)}${selectedPreset ? `&preset_id=${selectedPreset.id}` : ''}`
          );
        }, 150);
      },
      onError() {
        alert(t('잠시 후 다시 시도해주세요.'));
      },
    });
  };

  return (
    <Modal {...props} header={false} hideCloseButton>
      <div css={containerCSS}>
        {step === 'episode-select' && (
          <>
            <p className="modal-title">{t('무료로 대화해보세요!')}</p>
            <p className="modal-subtitle">
              {t('대화할 회차를 선택하세요')}
            </p>

            {isLoadingEpisodes ? (
              <div className="loading-container">
                <Loading />
              </div>
            ) : episodes.length === 0 ? (
              <p className="empty-message">
                {t('대화 가능한 회차가 없습니다.')}
              </p>
            ) : (
              <div className="episode-list">
                {episodes.map((ep: IEpisode, idx: number) => (
                  <div
                    key={ep.id}
                    className={`episode-item ${selectedEpisode?.id === ep.id ? 'selected' : ''}`}
                    onClick={() => setSelectedEpisode(ep)}
                  >
                    <span className="episode-order">{idx + 1}화</span>
                    <span className="episode-title">{ep.title}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="btn-row">
              <Button
                className="send-btn"
                onClick={handleSelectEpisode}
                text={t('다음')}
                disabled={!selectedEpisode}
              />
              <Button
                type="text"
                text={t('닫기')}
                onClick={() => props.onClose()}
              />
            </div>
          </>
        )}

        {step === 'chat-input' && (
          <>
            {isLoadingCharacters ? (
              <div className="loading-container">
                <Loading />
                <span className="loading-text">
                  {t('캐릭터를 불러오고 있어요...')}
                </span>
              </div>
            ) : (
              <>
                {hasFreePreset && (
                  <div className="free-badge">
                    <span>🎉 {t('무료로 새로운 대화모드가 나왔어요!')}</span>
                    <span className="free-badge-sub">
                      {t('종료 시기 미정 · 지금 바로 체험해보세요')}
                    </span>
                  </div>
                )}
                <p className="modal-title">
                  {t(
                    `${me?.nickname} 님은\n지금부터 이 장면 속으로 들어갑니다.`
                  )}
                </p>
                <p className="modal-subtitle">
                  {t(
                    '내가 빙의하고 싶은 주인공 중 한 명을 골라\n하고 싶은 말을 적어보세요.'
                  )}
                </p>

                <SwipeableCharacterList
                  characterList={characters}
                  selectedCharacter={selectedCharacter}
                  onClickCharacter={(character) =>
                    setSelectedCharacter(character)
                  }
                />

                <div className="input-container">
                  <Textarea
                    className="text-form"
                    fontSize={14}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={t(
                      '이 장면의 주인공이 된다면 뭐라고 말할까요?'
                    )}
                  />
                </div>

                <div className="btn-row">
                  {hasMultiplePresets && presets && (
                    <div className="preset-selector">
                      {presets.map((preset) => {
                        const price =
                          preset.discount_message_price ?? preset.message_price;
                        const isFree = price === 0;
                        const isSelected = selectedPreset?.id === preset.id;
                        return (
                          <div
                            key={preset.id}
                            css={presetBtnCSS(isSelected)}
                            onClick={() => setSelectedPreset(preset)}
                          >
                            <div className="preset-name">
                              {isFree ? t('무료모델') : preset.name}
                            </div>
                            <div className="preset-price">
                              {isFree ? t('0풍선') : t(`${price}풍선`)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="desc">
                    {t('무료 대화권 보유 시 먼저 사용되며, 이후 메시지당 ')}
                    <span className="price-highlight">{selectedPrice}풍선</span>
                    {t('이 차감돼요.')}
                  </div>
                  <Button
                    className="send-btn"
                    onClick={handleSend}
                    text={t('장면 속에 들어가기 👀')}
                    disabled={
                      !value || value.length < 2 || !selectedCharacter
                    }
                  />
                  <Button
                    type="text"
                    text={t('뒤로')}
                    onClick={() => {
                      setStep('episode-select');
                      setCharacters([]);
                      setSelectedCharacter(null);
                      setChatieData(null);
                      setValue('');
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default FreeChatEpisodeSelectModal;
